import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { Currency } from 'depoto-core/src/entities'
import { CoreService } from '../../services'
import { priceLevelsColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { PaymentService } from '../../services/entity-services/payment.service'
import { PriceLevelService } from '../../services/entity-services/priceLevel.service'

const DEFAULT_FILTERS = {
  name: '',
  isPercentage: 'all',
  withVat: 'all',
  currency: 'all',
}

@Component({
  selector: 'app-price-levels',
  templateUrl: './price-levels.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PriceLevelsComponent extends BaseListing implements OnInit {
  private priceLevelService = inject(PriceLevelService)
  private paymentService = inject(PaymentService)

  public listingColumns = signal(priceLevelsColumns)
  public booleanFilterOptions = ['all', 'yes', 'no']

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies'],
    queryFn: async () => {
      return await this.paymentService
        .getCurrencies({}, { id: null, name: null, ratio: null })
        .then(result => result.items)
    },
  }))

  readonly currenciesOptions = computed<Currency[]>(() => {
    const items = this.queryCurrencies.data() ?? []
    return [{ id: 'all', name: '', ratio: null }, ...items]
  })

  readonly queryFilters = computed(() => {
    const value = this.filters()
    const options: any = {}

    if (value.name?.length) options.name = value.name
    if (value.isPercentage !== 'all') options.isPercentage = value.isPercentage === 'yes'
    if (value.withVat !== 'all') options.withVat = value.withVat === 'yes'
    if (value.currency !== 'all') options.currency = value.currency

    return options
  })

  readonly queryPriceLevels = injectInfiniteQuery(() => ({
    queryKey: ['price-levels', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      return await this.priceLevelService
        .getPriceLevels(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          {
            id: null,
            name: null,
            isPercentage: null,
            percent: null,
            withVat: null,
            currency: { id: null, name: null },
          }
        )
        .then(result => {
          this.saveViewSettings('priceLevels', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryPriceLevels.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryCurrencies.data()
      this.queryPriceLevels.data()
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('priceLevels')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('priceLevels')
  }

  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }

  public clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryPriceLevels.refetch()
  }
}
